import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Zoom from '@material-ui/core/Zoom';
import CircularProgress from '@material-ui/core/CircularProgress';
import GreenChecked from '../../images/icons/greenChecked.svg';

import ResourceUploader from '../form/ResourceUploader';
import Note from '../../components/ui/Note';
import Transition from './Transition';

const styles = (theme) => ({
	dialogPadding: {
		padding: 10,
		[theme.breakpoints.up('md')]: {
			padding: '20px 50px 50px 50px',
		},
	},
	dialogPaper: {
		overflow: 'scroll',
		maxWidth: '100%',
		width: 800,
		maxHeight: 'calc(100% - 20px)',
		margin: 10,
	},
	greenCheckedIcon: {
		display: 'inline-block',
		width: 33,
		height: 33,
		backgroundImage: `url('${GreenChecked}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		marginRight: 2,
		marginLeft: 5,
	},
	isSaving: {
		marginLeft: 20,
		fontSize: 16,
		color: '#1565C0',
	},
	savingSuccessful: {
		marginLeft: 20,
		fontSize: 16,
		color: '#2E7D32',
	},
	parsedSummary: {
		marginTop: 20,
		padding: 15,
		border: '1px solid #E0E0E0',
		borderRadius: 6,
		backgroundColor: '#FAFAFA',
	},
	parsedRow: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '2px 0',
	},
	parseErrors: {
		marginTop: 15,
		color: '#C62828',
	},
	parseWarnings: {
		marginTop: 15,
		color: '#E65100',
	},
	matchResult: {
		marginTop: 15,
	},
	duplicateNotice: {
		marginTop: 15,
		padding: 12,
		borderRadius: 6,
		backgroundColor: '#FFF8E1',
		color: '#E65100',
		fontWeight: 'bold',
	},
	bundleRow: {
		padding: '6px 0',
		borderBottom: '1px solid #EEEEEE',
	},
});

const parsedFieldLabels = [
	['bolNumber', 'BOL #'],
	['loadingNumber', 'Pearson ID (Delivery #)'],
	['railcarNumber1', 'Railcar #1'],
	['railcarNumber2', 'Railcar #2'],
	['railcarNumber3', 'Railcar #3'],
	['carrierRaw', 'Carrier'],
	['product', 'Product'],
	['grossGallons', 'Gross Gallons'],
	['netGallons', 'Net Gallons'],
	['startDateTime', 'BOL Date'],
];

function isDateTimeKey(key) {
	return key === 'startDateTime';
}

/**
 * A one-line status for a page of an uploaded bundle (or a single-page
 * upload): success/duplicate/error plus the link outcome.
 */
function pageResultSummary(result) {
	if (!result) {
		return 'No result';
	}
	if (result.error) {
		return `Error: ${result.error}`;
	}
	if (result.duplicate) {
		return `Duplicate — already uploaded (row #${result.existingInterstateBolId})`;
	}
	if (!_.isEmpty(result.parseErrors)) {
		return `Parse errors: ${result.parseErrors.join('; ')}`;
	}
	const linkedStatus = _.get(result, ['matchResult', 'linkedStatus']);
	if (linkedStatus === 'railcarLinked') {
		return 'Linked ✓';
	}
	return (
		_.get(result, ['matchResult', 'linkedErrorMessage']) ||
		`Not fully linked (${linkedStatus || 'unknown'})`
	);
}

class InterstateBolUploadDialog extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isSaving: false,
			savingSuccessful: false,
			lastParsed: null,
			parseErrors: [],
			parseWarnings: [],
			matchResult: null,
			duplicateId: null,
			bundleResults: null,
		};
	}

	handleFileUpload = ({ resource }) => {
		const { uploadBol, showErrors, onAfterSave } = this.props;
		const bolResourceId = resource.resourceId;

		this.setState({
			isSaving: true,
			savingSuccessful: false,
			duplicateId: null,
			bundleResults: null,
		});

		uploadBol({
			params: {
				bolResourceId,
				fileName: resource.originalName || resource.name || null,
			},
			callback: (err, result) => {
				if (err) {
					this.setState({ isSaving: false });
					showErrors(err);
					return;
				}

				// A multi-BOL bundle: one result per page.
				if (result && result.bundle) {
					this.setState({
						isSaving: false,
						savingSuccessful: true,
						lastParsed: null,
						parseErrors: [],
						parseWarnings: [],
						matchResult: null,
						duplicateId: null,
						bundleResults: result.results,
					});
					onAfterSave();
					return;
				}

				if (result && result.duplicate) {
					this.setState({
						isSaving: false,
						savingSuccessful: false,
						lastParsed: _.get(result, 'parsedData', null),
						parseErrors: [],
						parseWarnings: [],
						matchResult: null,
						duplicateId: result.existingInterstateBolId,
					});
					return;
				}

				this.setState({
					isSaving: false,
					savingSuccessful: true,
					lastParsed: _.get(result, 'parsedData', null),
					parseErrors: _.get(result, 'parseErrors', []),
					parseWarnings: _.get(result, 'parseWarnings', []),
					matchResult: _.get(result, 'matchResult', null),
					duplicateId: null,
				});
				onAfterSave();
			},
		});
	};

	handleClose = () => {
		this.props.onClose();
	};

	render() {
		const { classes, fullScreen, open } = this.props;
		const {
			isSaving,
			savingSuccessful,
			lastParsed,
			parseErrors,
			parseWarnings,
			matchResult,
			duplicateId,
			bundleResults,
		} = this.state;

		return (
			<Dialog
				fullScreen={fullScreen}
				maxWidth="md"
				open={open}
				TransitionComponent={Transition}
				onClose={this.handleClose}
				classes={{ paper: classes.dialogPaper }}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle style={{ paddingBottom: 5 }}>Upload InterState Oil BOL</DialogTitle>
					<div>
						<Note>
							Upload the scanned InterState Oil Straight Bill of Lading PDF. A multi-page scan is
							treated as a bundle — one complete BOL per page — and split automatically. We read
							the Pearson ID, BOL #, railcar(s), and gallons off each page using AI vision (15-30
							seconds per page), then auto-link each BOL to the matching station delivery and
							railcar(s).
						</Note>
					</div>

					<DialogContent>
						<ResourceUploader
							name={'interstateBolPdf'}
							label={''}
							afterUpload={this.handleFileUpload}
						/>
					</DialogContent>

					{bundleResults && (
						<div className={classes.parsedSummary}>
							<strong>Bundle processed — {bundleResults.length} pages</strong>
							{bundleResults.map((pageResult, i) => (
								<div className={classes.bundleRow} key={i}>
									<div>
										<strong>
											{_.get(pageResult, 'fileName') || `Page ${i + 1}`}
											{pageResult.bolNumber ? ` — BOL #${pageResult.bolNumber}` : ''}
										</strong>
									</div>
									<div>{pageResultSummary(pageResult)}</div>
								</div>
							))}
						</div>
					)}

					{lastParsed && (
						<div className={classes.parsedSummary}>
							<strong>Parsed BOL</strong>
							{parsedFieldLabels.map(([key, label]) => {
								let value;
								if (isDateTimeKey(key) && lastParsed[key]) {
									// The parser returns these as naked UTC strings, so parse as
									// UTC and render local to match the InterState BOLs grid.
									value = moment.utc(lastParsed[key]).local().format('M/D/YY h:mm a');
								} else {
									value = lastParsed[key];
								}
								if (key === 'loadingNumber' && lastParsed.pearsonIdHandwritten) {
									value = `${value} (handwritten)`;
								}
								return (
									<div className={classes.parsedRow} key={key}>
										<span>{label}</span>
										<span>
											<strong>{_.isNil(value) ? '-' : String(value)}</strong>
										</span>
									</div>
								);
							})}
						</div>
					)}

					{duplicateId && (
						<div className={classes.duplicateNotice}>
							This BOL was already uploaded (row #{duplicateId}). No new record was created.
						</div>
					)}

					{!_.isEmpty(parseErrors) && (
						<div className={classes.parseErrors}>
							<strong>Could not read required fields (not linked):</strong>
							<ul>
								{parseErrors.map((e, i) => (
									<li key={i}>{e}</li>
								))}
							</ul>
						</div>
					)}

					{!_.isEmpty(parseWarnings) && (
						<div className={classes.parseWarnings}>
							<strong>Some optional fields were not read:</strong>
							<ul>
								{parseWarnings.map((w, i) => (
									<li key={i}>{w}</li>
								))}
							</ul>
						</div>
					)}

					{matchResult && (
						<div className={classes.matchResult}>
							<strong>Link result:</strong> {matchResult.linkedStatus}
							{matchResult.linkedErrorMessage && <div>{matchResult.linkedErrorMessage}</div>}
						</div>
					)}

					<div style={{ display: 'flex', alignItems: 'baseline', marginTop: 15 }}>
						<Button variant="contained" onClick={this.handleClose}>
							Close
						</Button>

						<Zoom in={isSaving} mountOnEnter unmountOnExit>
							<div className={classes.isSaving}>
								<CircularProgress style={{ verticalAlign: 'middle' }} />
								<span style={{ paddingLeft: 10 }}>Processing...</span>
							</div>
						</Zoom>

						<Zoom in={savingSuccessful}>
							<div className={classes.savingSuccessful}>
								<div className={classes.greenCheckedIcon} />
								<span style={{ paddingLeft: 10 }}>
									Uploaded and processed. Upload another or close.
								</span>
							</div>
						</Zoom>
					</div>
				</div>
			</Dialog>
		);
	}
}

InterstateBolUploadDialog.propTypes = {
	uploadBol: PropTypes.func.isRequired,
	showErrors: PropTypes.func.isRequired,
	onAfterSave: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	open: PropTypes.bool,
	classes: PropTypes.object.isRequired,
	fullScreen: PropTypes.bool.isRequired,
};

export default _.flow([withStyles(styles), withMobileDialog()])(InterstateBolUploadDialog);
